import { CardContainer } from "@msys/ui";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";

type EventEntry = {
  // PdsSyncEvent (pds status query) or IntegrationEvent (settings/dashboard)
  __typename: "PdsSyncEvent" | "IntegrationEvent";
  type: string;
  title: string;
  detail?: string | null;
  metadata?: any;
  createdAt: string;
};

type GroupEntry = {
  __typename: "PdsSyncEventGroup" | "IntegrationEventGroup";
  type: string;
  count: number;
  timeRangeFrom: string;
  timeRangeTo: string;
  metadata?: any;
};

export type ActivityFeedEntry = EventEntry | GroupEntry;

function isGroupEntry(entry: ActivityFeedEntry): entry is GroupEntry {
  return (
    entry.__typename === "PdsSyncEventGroup" ||
    entry.__typename === "IntegrationEventGroup"
  );
}

interface Props {
  events: ActivityFeedEntry[];
  onRefresh?: () => Promise<unknown>;
}

const eventIconColors: Record<string, string> = {
  heartbeat: "#4caf50",
  heartbeat_group: "#4caf50",
  tier1_sync: "#1565C0",
  tier1_sync_group: "#1565C0",
  full_sync: "#5c6bc0",
  full_sync_group: "#5c6bc0",
  version_hash_mismatch: "#ff9800",
  version_change: "#ff9800",
  version_unsupported: "#ef5350",
  settings_updated: "#00897b",
  settings_updated_group: "#00897b",
  config_updated: "#00897b",
  config_updated_group: "#00897b",
  config_apply_failed: "#ef5350",
  // MEIS-13196: a cancel is neither a completion (the tier blues) nor a
  // failure (the reds) — a user stopped the sync on purpose. Neutral
  // blue-grey keeps it readable as its own outcome in the feed.
  tier1_sync_cancelled: "#78909c",
  tier1_sync_cancelled_group: "#78909c",
  full_sync_cancelled: "#78909c",
  full_sync_cancelled_group: "#78909c",
  sync: "#1565C0",
  detect: "#ff9800",
  night: "#5c6bc0",
};

// Event metadata is free-form JSON from the events table — narrow each
// param instead of trusting the shape
function metaNumber(metadata: unknown, key: string): number | null {
  if (!metadata || typeof metadata !== "object") return null;
  const value = (metadata as Record<string, unknown>)[key];
  return typeof value === "number" ? value : null;
}

function metaString(metadata: unknown, key: string): string | null {
  if (!metadata || typeof metadata !== "object") return null;
  const value = (metadata as Record<string, unknown>)[key];
  return typeof value === "string" ? value : null;
}

export const ActivityFeed = ({ events, onRefresh }: Props) => {
  const { t } = useTranslate(["Integrations", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const [refreshing, setRefreshing] = React.useState(false);

  // Heartbeats are now excluded server-side, before the row limit is applied
  // (MEIS-13089, D5), so this filter is belt-and-braces only (e.g. rows
  // already cached from before that change). There is no distinct
  // failed-heartbeat event type today; failures surface via health, not here.
  const visibleEvents = React.useMemo(
    () =>
      events.filter(
        e => e.type !== "heartbeat" && e.type !== "heartbeat_group"
      ),
    [events]
  );

  const handleRefresh = async () => {
    if (!onRefresh || refreshing) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } catch {
      enqueueSnackbar(
        t("Something went wrong. Please try again.", { ns: "Global" }),
        { variant: "error" }
      );
    } finally {
      setRefreshing(false);
    }
  };

  // Stored titles/details are pre-baked German strings (written by the
  // workflows at event time). Rebuild them in the user's language from
  // type + metadata; events whose params are missing (written before the
  // metadata fields existed) fall back to the stored text.
  const eventTitle = (event: EventEntry): string => {
    const m: unknown = event.metadata;
    switch (event.type) {
      case "heartbeat": {
        const number = metaNumber(m, "heartbeatNumber");
        const flag = metaString(m, "dirtyFlag");
        return number !== null && flag !== null
          ? t("Heartbeat #{number}: {flag}", {
              number,
              flag,
              ns: "Integrations",
            })
          : event.title;
      }
      case "tier1_sync":
        return t("Active customer sync completed");
      case "full_sync":
        return t("Full sync completed");
      case "tier1_sync_cancelled":
        return t("Active customer sync cancelled");
      case "full_sync_cancelled":
        return t("Full sync cancelled");
      case "task_execution": {
        const executed = metaNumber(m, "executed");
        if (executed === null) return event.title;
        const failed = metaNumber(m, "failed");
        return failed
          ? t("{executed} tasks executed, {failed} failed", {
              executed,
              failed,
              ns: "Integrations",
            })
          : t("{executed} tasks executed", { executed, ns: "Integrations" });
      }
      case "dirty_new_sync": {
        const count = metaNumber(m, "count");
        return count !== null
          ? t("{count} new customers synchronized", {
              count,
              ns: "Integrations",
            })
          : event.title;
      }
      case "config_updated":
        return t("Configuration updated");
      case "settings_updated":
        return t("Settings updated");
      case "config_apply_failed":
        return t("Settings could not be delivered to the running sync");
      case "version_change": {
        const oldVersion = metaString(m, "oldVersion");
        const newVersion = metaString(m, "newVersion");
        return oldVersion !== null && newVersion !== null
          ? t("PDS API version: {oldVersion} → {newVersion}", {
              oldVersion,
              newVersion,
              ns: "Integrations",
            })
          : event.title;
      }
      case "version_hash_mismatch": {
        const version = metaString(m, "version");
        return version !== null
          ? t("Spec hash mismatch for version {version}", {
              version,
              ns: "Integrations",
            })
          : event.title;
      }
      case "version_unsupported": {
        const version = metaString(m, "version");
        return version !== null
          ? t("Sync paused: version {version} not supported", {
              version,
              ns: "Integrations",
            })
          : event.title;
      }
      default:
        return event.title;
    }
  };

  const eventDetail = (event: EventEntry): string | null => {
    const m: unknown = event.metadata;
    switch (event.type) {
      case "tier1_sync": {
        const total = metaNumber(m, "total");
        const tasksCreated = metaNumber(m, "tasksCreated");
        const directUpdates = metaNumber(m, "directUpdates");
        const skipped = metaNumber(m, "skipped");
        return total !== null &&
          tasksCreated !== null &&
          directUpdates !== null &&
          skipped !== null
          ? t(
              "{total} customers. {tasksCreated} tasks created, {directUpdates} direct updates, {skipped} unchanged.",
              {
                total,
                tasksCreated,
                directUpdates,
                skipped,
                ns: "Integrations",
              }
            )
          : (event.detail ?? null);
      }
      case "full_sync": {
        const total = metaNumber(m, "totalCustomers");
        const tasksCreated = metaNumber(m, "tasksCreated");
        const directUpdates = metaNumber(m, "directUpdates");
        return total !== null && tasksCreated !== null && directUpdates !== null
          ? t(
              "{total} customers. {tasksCreated} tasks created, {directUpdates} direct updates.",
              { total, tasksCreated, directUpdates, ns: "Integrations" }
            )
          : (event.detail ?? null);
      }
      default:
        return event.detail ?? null;
    }
  };

  const groupLabel = (type: string): string => {
    switch (type.replace("_group", "")) {
      case "heartbeat":
        return t("Heartbeat");
      case "tier1_sync":
        return t("Active customer sync");
      case "full_sync":
        return t("Full sync");
      // Consecutive cancels group like any other type (the backend appends
      // `_group` to whatever type repeats — IntegrationService's
      // groupConsecutiveEvents has no whitelist), so the grouped form needs
      // the same localized label the single event gets from eventTitle.
      case "tier1_sync_cancelled":
        return t("Active customer sync cancelled");
      case "full_sync_cancelled":
        return t("Full sync cancelled");
      case "task_execution":
        return t("Task execution");
      case "dirty_new_sync":
        return t("New customers synchronized");
      case "config_updated":
        return t("Configuration updated");
      case "settings_updated":
        return t("Settings updated");
      default:
        return type
          .replace("_group", "")
          .replace(/_/g, " ")
          .replace(/\b\w/g, c => c.toUpperCase());
    }
  };

  return (
    <CardContainer
      title={t("Recent activity")}
      itemCount={visibleEvents.length}
      ActionButton={
        onRefresh ? (
          <IconButton
            size="small"
            aria-label={t("Refresh", { ns: "Global" })}
            title={t("Refresh", { ns: "Global" })}
            disabled={refreshing}
            onClick={e => {
              // The card header may be clickable (expand) — keep the
              // refresh from toggling it
              e.stopPropagation();
              void handleRefresh();
            }}
          >
            <RefreshIcon
              fontSize="small"
              sx={
                refreshing
                  ? {
                      animation: "activity-refresh-spin 1s linear infinite",
                      "@keyframes activity-refresh-spin": {
                        from: { transform: "rotate(0deg)" },
                        to: { transform: "rotate(360deg)" },
                      },
                    }
                  : undefined
              }
            />
          </IconButton>
        ) : undefined
      }
    >
      <Stack spacing={0} sx={{ p: 1 }}>
        {visibleEvents.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t("No events yet")}
          </Typography>
        )}
        {visibleEvents.map((event, i) => (
          <Stack
            key={i}
            direction="row"
            spacing={1}
            sx={{
              justifyContent: "space-between",
              alignItems: "flex-start",
              py: 0.75,
              borderBottom: i < visibleEvents.length - 1 ? "1px solid" : "none",
              borderColor: "divider",
            }}
          >
            {!isGroupEntry(event) ? (
              <>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "center" }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: 0.5,
                        backgroundColor:
                          eventIconColors[event.type] ?? "#9e9e9e",
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, minWidth: 0 }}
                    >
                      {eventTitle(event)}
                    </Typography>
                  </Stack>
                  {eventDetail(event) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        // Indent under the title (dot width + gap)
                        pl: 2,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {eventDetail(event)}
                    </Typography>
                  )}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ flexShrink: 0, fontFamily: "monospace" }}
                >
                  {new Date(event.createdAt).toLocaleString()}
                </Typography>
              </>
            ) : (
              <>
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{ alignItems: "center" }}
                >
                  {Array.from({ length: Math.min(event.count, 8) }).map(
                    (_, j) => (
                      <Box
                        key={j}
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          backgroundColor:
                            eventIconColors[event.type] ?? "#9e9e9e",
                          opacity: 1 - j * 0.1,
                        }}
                      />
                    )
                  )}
                  <Typography variant="body2" sx={{ ml: 0.5 }}>
                    {event.count}x {groupLabel(event.type)}
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ flexShrink: 0, fontFamily: "monospace" }}
                >
                  {new Date(event.timeRangeFrom).toLocaleTimeString()}
                  {" – "}
                  {new Date(event.timeRangeTo).toLocaleTimeString()}
                </Typography>
              </>
            )}
          </Stack>
        ))}
      </Stack>
    </CardContainer>
  );
};
